import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import routes from "../../routes";
import SurveyModal from "../../commons/components/SurveyModal";
import { refreshBasicData } from "../../commons/services/store/commons.actions";
import LoadingSpinner from "../../_UI/Components/LoadingSpinner";
import { ErrorMessage } from "../../_UI/Components/Alerts/PathwayAlertMessages";
import { getResponseNotification } from "../../notification/store/notification.actions";
import NotificationModal from "../../notification/components/NotificationModal";
import { selectOrganization, sendCelloRegistrationData } from "../../user/services/store/user.actions";
import NavRail from "./rail/NavRail";
import styled from "styled-components";
import GlobalAlert from "./GlobalAlert";
import useCelloAttribution from "../../_hooks/useCelloAttribution";
import useUserAuthentication from "../../_hooks/useUserAuthentication";
import { OrganizationType } from "../../_statics/OrganizationType";
import { OrganizationContext } from "../../integrations/_commons/PlatformContext";
import { setupActions } from "../../user/services/store/setup.actions";
import { adminAreaActions } from "../../admin/services/store/adminarea.actions";
import { mobile } from "../../design-system/tokens/breakpoints";

const DefaultLayout = () => {
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    const commons = useSelector((state) => state.commons);
    const authentication = useSelector((state) => state.authentication);

    const user = useSelector((state) => state.user);
    const selectedOrganization = useSelector((state) =>
        state.authentication.user ? state.authentication.user.selectedOrganization : undefined
    );

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { ucc } = useCelloAttribution();
    const { isSuperUser } = useUserAuthentication();

    const hasCelloRun = useRef(false);

    useEffect(() => {
        if (hasCelloRun.current) return;
        if (ucc && authentication.user.externalUUID && selectedOrganization?.externalUUID && !isSuperUser) {
            dispatch(sendCelloRegistrationData(ucc, authentication.user));
            hasCelloRun.current = true;
        }
    }, [ucc, dispatch, isSuperUser, authentication.user, authentication.user?.externalUUID, selectedOrganization]);

    useEffect(() => {
        if (location && location.search && new URLSearchParams(location.search).has("organization")) {
            dispatch(
                selectOrganization(new URLSearchParams(location.search).get("organization"), true, location.pathname)
            );
        } else {
            dispatch(refreshBasicData());
            dispatch(setupActions.getSetupStatus());
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isSuperUser) {
            dispatch(adminAreaActions.getActiveMilestones());
            dispatch(adminAreaActions.getProjectLabels());
        }
    }, [isSuperUser, dispatch]);

    useEffect(() => {
        if (location && location.search && new URLSearchParams(location.search).has("notificationId")) {
            dispatch(getResponseNotification(new URLSearchParams(location.search).get("notificationId")));
        }
    }, [dispatch, location]);

    // Moved here from DefaultHeader, which the rail replaced: a logged-in user
    // without a selected organization cannot use the app and must sign out.
    useEffect(() => {
        if (authentication.user && authentication.user.loggedIn && !authentication.user.selectedOrganization) {
            navigate("/dologout");
        }
    }, [authentication.user, navigate]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const notificationId = searchParams.get("notificationId");

        if (
            selectedOrganization &&
            selectedOrganization.organizationType === OrganizationType.LEX_OFFICE &&
            !location.pathname.startsWith("/lexoffice")
        ) {
            navigate("/lexoffice");
        }
        if (
            selectedOrganization &&
            authentication.error &&
            selectedOrganization.error === "User has no Organization selected."
        ) {
            const loginUrl = notificationId ? `/login?notificationId=${notificationId}` : "/login";
            navigate(loginUrl);
        }
    }, [selectedOrganization, authentication, navigate, location]);

    const isLoading = useMemo(() => {
        return !!(commons.basicDataLoading || user.switchOrganizationLoading);
    }, [commons.basicDataLoading, user.switchOrganizationLoading]);

    return (
        <div>
            <OrganizationContext.Provider value={selectedOrganization}>
                <NotificationModal />

                <SurveyModal />

                <AppBody className="app-body">
                    <NavRail mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

                    <main className="main">
                        <MobileBar>
                            <HamburgerBtn
                                type="button"
                                onClick={() => setMobileNavOpen(true)}
                                aria-label="Navigation öffnen"
                                aria-expanded={mobileNavOpen}
                            >
                                <i className="fa fa-bars" aria-hidden="true" />
                            </HamburgerBtn>
                            <MobileBrandName>pathway</MobileBrandName>
                        </MobileBar>
                        <GlobalAlert />

                        <ContentContainer>
                            {isLoading ? (
                                <LoadingSpinner loading={isLoading} size="4rem" />
                            ) : commons.basicDataErrorMessage ? (
                                <ErrorMessage marginless>{commons.basicDataErrorMessage}</ErrorMessage>
                            ) : (
                                <Routes>
                                    {routes.map((route, idx) => {
                                        return route.component ? (
                                            <Route
                                                key={idx}
                                                path={route.path}
                                                exact={!!route.exact}
                                                name={route.name}
                                                element={
                                                    route.superUserOnly && !isSuperUser ? (
                                                        <Navigate to="/" replace />
                                                    ) : (
                                                        <route.component />
                                                    )
                                                }
                                            />
                                        ) : null;
                                    })}
                                </Routes>
                            )}
                        </ContentContainer>
                    </main>
                </AppBody>
            </OrganizationContext.Provider>
        </div>
    );
};

export default DefaultLayout;

const AppBody = styled.div`
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    align-items: start;

    background: var(--pw-bg-app);
    min-height: 100vh;

    ${mobile} {
        grid-template-columns: minmax(0, 1fr);
        overflow-x: hidden;
    }
`;

// The prototype's content inset. This used to come from the breadcrumb strip's own
// height and margin, so removing that strip left the content flush against the top edge.
// The bottom is deeper than the prototype's 40px because Intercom's launcher is a fixed
// widget we cannot restyle and it floats over whatever ends at the bottom right.
// `fluid` on purpose: Bootstrap's fixed .container caps the page at 1520px and
// centres it, which leaves several hundred pixels of dead space either side of
// the wide admin tables on large screens.
const ContentContainer = styled(Container).attrs({ fluid: true })`
    padding: 26px 30px 96px;

    ${mobile} {
        padding: 16px 16px 80px;
    }
`;

const MobileBar = styled.div`
    display: none;

    ${mobile} {
        display: flex;
        align-items: center;
        gap: 12px;
        position: sticky;
        top: 0;
        z-index: calc(var(--pw-z-rail) - 1);
        padding: 0 16px;
        height: 52px;
        background: var(--surface-rail);
        border-bottom: 1px solid var(--border-hairline);
    }
`;

const HamburgerBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: var(--pw-radius-md);
    background: transparent;
    color: var(--text-heading);
    font-size: 18px;
    cursor: pointer;
    transition: background var(--pw-transition-fast) var(--pw-ease);

    &:hover {
        background: var(--pw-neutral-100);
    }
`;

const MobileBrandName = styled.span`
    font-family: var(--font-display);
    font-size: var(--pw-text-md);
    font-weight: var(--pw-weight-bold);
    color: var(--text-heading);
`;
